// ============================================================
// ANSICHTSSACHE-AGENT – Schieberegler
// ============================================================
// Ein gestaltetes `input type="range"` — dieselbe Regel wie beim
// Kaestchen: das Bedienelement des Betriebssystems bringt eine
// fremde Farbe und eine fremde Form mit. Gezeichnet werden Spur
// und Griff selbst; Tastatur (Pfeile, Bild auf/ab, Pos1/Ende) und
// Bildschirmleser bleiben die des Browsers.
//
// Mit `label` steht die Beschriftung darueber und der Wert rechts
// daneben (`valueLabel`, fertig formatiert). `onCommit` meldet den
// Wert, wenn der Griff losgelassen ist oder die Tastatur ihn
// gesetzt hat — zum Speichern, ohne bei jedem Pixel zu schreiben.
// ============================================================

"use client";

import { useId } from "react";
import { cn } from "@/lib/utils";
import { FieldLabel } from "./field-label";

export interface SliderProps {
  value: number;
  min: number;
  max: number;
  step?: number;
  onChange: (value: number) => void;
  /** Der Wert ist gesetzt: losgelassen oder per Tastatur. */
  onCommit?: (value: number) => void;
  label?: React.ReactNode;
  /** Der Wert zum Lesen, rechts neben der Beschriftung. */
  valueLabel?: React.ReactNode;
  /** Unter der Spur: was links und was rechts bedeutet. */
  ends?: [React.ReactNode, React.ReactNode];
  disabled?: boolean;
  className?: string;
  "aria-label"?: string;
}

export function Slider({
  value,
  min,
  max,
  step = 1,
  onChange,
  onCommit,
  label,
  valueLabel,
  ends,
  disabled,
  className,
  "aria-label": ariaLabel,
}: SliderProps) {
  const id = useId();
  const commit = (event: React.SyntheticEvent<HTMLInputElement>) =>
    onCommit?.(Number(event.currentTarget.value));

  return (
    <div className={cn("w-full", className)}>
      {(label || valueLabel) && (
        <div className="mb-2 flex items-baseline justify-between gap-3">
          {label && <FieldLabel htmlFor={id}>{label}</FieldLabel>}
          {valueLabel && (
            <span className="text-body-sm text-text-primary numeric">{valueLabel}</span>
          )}
        </div>
      )}
      <input
        id={id}
        type="range"
        min={min}
        max={max}
        step={step}
        value={value}
        disabled={disabled}
        aria-label={ariaLabel}
        onChange={(event) => onChange(Number(event.currentTarget.value))}
        onPointerUp={commit}
        onKeyUp={commit}
        className={cn(
          "block h-4 w-full cursor-pointer appearance-none bg-transparent focus-visible:outline-none",
          "disabled:cursor-not-allowed disabled:opacity-50",
          // Spur. Die Klassen stehen ausgeschrieben da, weil Tailwind
          // nur findet, was woertlich im Quelltext steht.
          "[&::-webkit-slider-runnable-track]:bg-border [&::-webkit-slider-runnable-track]:h-1.5 [&::-webkit-slider-runnable-track]:rounded-full",
          "[&::-moz-range-track]:bg-border [&::-moz-range-track]:h-1.5 [&::-moz-range-track]:rounded-full",
          // Griff — in WebKit sitzt er oben auf der Spur und muss um
          // die halbe Differenz hinauf (16 px Griff, 6 px Spur).
          "[&::-webkit-slider-thumb]:border-accent [&::-webkit-slider-thumb]:bg-bg-primary [&::-webkit-slider-thumb]:-mt-[5px] [&::-webkit-slider-thumb]:h-4 [&::-webkit-slider-thumb]:w-4 [&::-webkit-slider-thumb]:appearance-none [&::-webkit-slider-thumb]:rounded-full [&::-webkit-slider-thumb]:border-2",
          "[&::-moz-range-thumb]:border-accent [&::-moz-range-thumb]:bg-bg-primary [&::-moz-range-thumb]:h-4 [&::-moz-range-thumb]:w-4 [&::-moz-range-thumb]:rounded-full [&::-moz-range-thumb]:border-2",
          "hover:[&::-webkit-slider-thumb]:border-accent-hover hover:[&::-moz-range-thumb]:border-accent-hover",
          "focus-visible:[&::-webkit-slider-thumb]:ring-accent/30 focus-visible:[&::-webkit-slider-thumb]:ring-[3px]",
          "focus-visible:[&::-moz-range-thumb]:ring-accent/30 focus-visible:[&::-moz-range-thumb]:ring-[3px]",
        )}
      />
      {ends && (
        <div className="text-caption text-text-tertiary mt-1 flex justify-between gap-3">
          <span>{ends[0]}</span>
          <span>{ends[1]}</span>
        </div>
      )}
    </div>
  );
}
